import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../../Styles/components/ai-assistant.scss";
import useAiApi from "../../api/aiAPI";
import ProgressTracker from "./cards/ProgressTracker";
import ChoiceCard from "./cards/ChoiceCard";
import UploadCard from "./cards/UploadCard";
import SummaryCard from "./cards/SummaryCard";
import QrCard from "./cards/QrCard";
import SuccessCard from "./cards/SuccessCard";
import QuestionCard from "./cards/QuestionCard";
import MerchantInformationAPI from "../../../api/MerchantInformationAPI";
import { MinimizeIcon, SparkleAvatar, SparkleIcon } from "./AssistantIcons";
import AiSuggestedActions from "./AiSuggestedActions";
import AiTodoPreview from "./AiTodoPreview";
import PendingApprovalCard from "./PendingApprovalCard";
import {
  buildDetailsRequestId,
  getTimeGreeting,
  normalizeDashboardApproval,
  normalizeWelcomeDashboard,
} from "./assistantDashboardUtils";
import { ACTION_TYPES } from "../../../app/actionTypes";
import {
  setActionPayload,
  setActionType,
} from "../../../features/authorizationSlice";
import { resetRequest } from "../../../features/transactionDetailsSlice";
import { setFeatureData } from "../../store/slices/appSlice";
import {
  DEFAULT_SAFE_FILE_ACCEPT,
  extractApiErrorMessage,
  formatAllowedFileTypes,
  formatApiMessageText,
  getApiErrorMessage,
  getFileExtension,
  getPortalValidationMessage,
  hasApiError,
  isSafeFileMimeType,
  normalizeAssistantResponse as N,
  resolveSafeFileExtensions,
} from "./assistantSearchUtils";

const AUTH_POLL = "/v1/Authorization/PortalActionsAuthorizationStatus";

const AssistantSearch = ({
  userName = "User",
  value = "",
  onChange,
  quickActions = [],
  variant = "",
  onQuickActionClick,
  onOpenQrAfterGenerate,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dashboardState = useSelector(
    (state) => state.app?.summary?.aiAssistantDashboard || {},
  );
  const [progressSteps, setProgressSteps] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [streamingBotText, setStreamingBotText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showTodoDashboard, setShowTodoDashboard] = useState(false);

  const busyRef = useRef(false);
  const dashboardRequestedRef = useRef(false);
  const activeCardRef = useRef(null);
  const streamingTargetRef = useRef("");
  const streamingVisibleRef = useRef("");
  const streamingFinalizeRef = useRef(false);
  const typingTimerRef = useRef(null);
  const focusTimeoutRef = useRef(null);

  const { SaveUserEmployeeSelectedMerchant } = MerchantInformationAPI();
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  useEffect(() => {
    activeCardRef.current = activeCard;
  }, [activeCard]);
  const guardBusy = () => {
    if (busyRef.current) return true;
    busyRef.current = true;
    setBusy(true);
    return false;
  };

  const api = useAiApi();
  const getWelcomeDashboardRef = useRef(api.GetWelcomeDashboard);
  getWelcomeDashboardRef.current = api.GetWelcomeDashboard;
  const fidRef = useRef(null);
  const cdRef = useRef({});
  const pfRef = useRef(null);
  const ptRef = useRef(null);
  const authorizeContextRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const excelUrlRef = useRef("");
  const scrollAreaRef = useRef(null);
  const lastUserMessageRef = useRef("");
  const messageIdRef = useRef(0);
  const previousBusyRef = useRef(busy);
  const [refreshingQr, setRefreshingQr] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const pendingApprovals = useMemo(
    () =>
      (
        dashboardState.TransactionsAwaitingApproval ||
        dashboardState.transactionsAwaitingApproval ||
        []
      ).map(normalizeDashboardApproval),
    [
      dashboardState.TransactionsAwaitingApproval,
      dashboardState.transactionsAwaitingApproval,
    ],
  );
  const pendingCount = pendingApprovals.length;
  const shouldShowTodoPreview =
    variant === "account-summary" && pendingCount > 0;
  const dashboardGreeting = useMemo(
    () => getTimeGreeting(userName),
    [userName],
  );
  const focusInputAtEnd = () => {
    const inputNode = inputRef.current;
    if (!inputNode || inputNode.disabled) return;

    const activeElement = document.activeElement;
    const isEditableTarget =
      activeElement &&
      activeElement !== inputNode &&
      (activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable);

    if (isEditableTarget) return;

    inputNode.focus({ preventScroll: true });
    const length = inputNode.value?.length || 0;
    inputNode.setSelectionRange?.(length, length);
  };

  const openChatPopup = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const handleQuickActionSelect = useCallback(
    (prompt) => {
      if (!prompt) return;

      setShowTodoDashboard(false);
      onChange?.(prompt);
      if (inputRef.current) {
        inputRef.current.value = prompt;
      }
      openChatPopup();

      requestAnimationFrame(() => {
        const length = inputRef.current?.value?.length || 0;
        inputRef.current?.focus({ preventScroll: true });
        inputRef.current?.setSelectionRange?.(length, length);
      });
    },
    [onChange, openChatPopup],
  );
  // ═══ WELCOME: call on mount ═══
  // ═══ WELCOME: call once per login ═══
  useEffect(() => {
    if (dashboardRequestedRef.current || dashboardState.loaded) return;

    let cancelled = false;
    dashboardRequestedRef.current = true;
    dispatch(
      setFeatureData({
        section: "summary",
        key: "aiAssistantDashboard",
        value: {
          loading: true,
          error: "",
        },
      }),
    );

    const loadDashboard = async () => {
      try {
        const response = await getWelcomeDashboardRef.current();
        if (cancelled) return;

        const dashboard = normalizeWelcomeDashboard(response);
        dispatch(
          setFeatureData({
            section: "summary",
            key: "aiAssistantDashboard",
            value: {
              loaded: true,
              loading: false,
              error: "",
              TransactionsAwaitingApproval:
                dashboard.transactionsAwaitingApproval,
              DailySpent: dashboard.dailySpent,
            },
          }),
        );
      } catch (error) {
        if (cancelled) return;

        dispatch(
          setFeatureData({
            section: "summary",
            key: "aiAssistantDashboard",
            value: {
              loaded: true,
              loading: false,
              error:
                error?.response?.data?.Message ||
                error?.message ||
                "Unable to load assistant dashboard.",
            },
          }),
        );
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [dashboardState.loaded, dispatch]);
  useEffect(() => {
    const node = scrollAreaRef.current;
    if (!node) return;
    node.scrollTo({
      top: node.scrollHeight,
      behavior: "smooth",
    });
  }, [
    messages,
    activeCard,
    progressSteps.length,
    errorMsg,
    streamingBotText,
    busy,
  ]);

  useEffect(() => {
    if (!inputRef.current) return;

    const nextValue = typeof value === "string" ? value : "";
    if (inputRef.current.value === nextValue) return;

    inputRef.current.value = nextValue;

    if (nextValue) {
      openChatPopup();
      requestAnimationFrame(() => {
        const length = inputRef.current?.value?.length || 0;
        inputRef.current?.setSelectionRange?.(length, length);
      });
    }
  }, [openChatPopup, value]);

  useEffect(() => {
    const wasBusy = previousBusyRef.current;
    previousBusyRef.current = busy;

    if (!wasBusy || busy) return;

    focusTimeoutRef.current = setTimeout(() => {
      focusInputAtEnd();
      focusTimeoutRef.current = null;
    }, 50);

    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
    };
  }, [busy]);

  useEffect(() => {
    streamingVisibleRef.current = streamingBotText;
  }, [streamingBotText]);

  const isFileDragEvent = (e) =>
    Array.from(e?.dataTransfer?.types || []).includes("Files");
  const clearDragState = () => setDragOver(false);
  const getSafeAcceptString = (accept) =>
    resolveSafeFileExtensions(accept).join(",");
  const getAllowedExtensions = (accept) =>
    resolveSafeFileExtensions(accept || DEFAULT_SAFE_FILE_ACCEPT);
  const clearPendingFile = () => {
    pfRef.current = null;
    delete cdRef.current.originalFileName;
    delete cdRef.current.fileSize;
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const stopTypewriter = () => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  };
  useEffect(
    () => () => {
      stopTypewriter();
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
      cancelRecording();
    },
    [],
  );
  const clearStreamingPreview = () => {
    stopTypewriter();
    streamingTargetRef.current = "";
    streamingVisibleRef.current = "";
    streamingFinalizeRef.current = false;
    setStreamingBotText("");
  };
  const commitStreamingPreview = () => {
    const finalText = streamingTargetRef.current.trim();
    if (finalText) appendBotMessage(finalText);
    clearStreamingPreview();
  };
  const TYPEWRITER_DELAY_MS = 45;
  const runTypewriter = () => {
    if (typingTimerRef.current) return;
    const tick = () => {
      typingTimerRef.current = null;
      const target = streamingTargetRef.current;
      const current = streamingVisibleRef.current;
      if (!target) return;

      if (current.length < target.length) {
        const remaining = target.length - current.length;
        const step = 1;
        const next = target.slice(0, current.length + step);
        streamingVisibleRef.current = next;
        setStreamingBotText(next);
      }

      if (
        streamingFinalizeRef.current &&
        streamingVisibleRef.current.length >= streamingTargetRef.current.length
      ) {
        commitStreamingPreview();
        return;
      }

      if (
        streamingVisibleRef.current.length <
          streamingTargetRef.current.length ||
        streamingFinalizeRef.current
      ) {
        typingTimerRef.current = setTimeout(tick, TYPEWRITER_DELAY_MS);
      }
    };
    typingTimerRef.current = setTimeout(tick, TYPEWRITER_DELAY_MS);
  };
  const queueStreamingText = (text) => {
    const nextText = String(text || "");
    streamingTargetRef.current = nextText;
    streamingFinalizeRef.current = false;
    setShowWelcome(false);
    if (!nextText) {
      clearStreamingPreview();
      return;
    }
    if (!nextText.startsWith(streamingVisibleRef.current)) {
      streamingVisibleRef.current = "";
      setStreamingBotText("");
    }
    runTypewriter();
  };
  const finalizeStreamingText = (text) => {
    const finalText = String(text || "");
    if (!finalText.trim()) {
      clearStreamingPreview();
      return;
    }
    streamingTargetRef.current = finalText;
    streamingFinalizeRef.current = true;
    runTypewriter();
  };
  const resetConversationState = (errorMessage = "") => {
    stopPoll();
    authorizeContextRef.current = null;
    fidRef.current = null;
    cdRef.current = {};
    excelUrlRef.current = "";
    busyRef.current = false;
    setActiveCard(null);
    setProgressSteps([]);
    setMessages([]);
    lastUserMessageRef.current = "";
    clearStreamingPreview();
    clearDragState();
    clearPendingFile();
    setIsOpen(false);
    setIsMinimized(false);
    setShowTodoDashboard(false);
    setShowWelcome(true);
    setErrorMsg(errorMessage);
    setBusy(false);
    setRefreshingQr(false);
    if (inputRef.current) inputRef.current.value = "";
    onChange?.("");
  };
  const validateSelectedFile = (file, accept) => {
    const allowedExtensions = getAllowedExtensions(accept);
    const allowedLabel = formatAllowedFileTypes(allowedExtensions);
    if (!file) return `Please choose a ${allowedLabel} file.`;
    if (file.size <= 0) return "The selected file is empty.";

    const extension = getFileExtension(file.name);
    if (!allowedExtensions.includes(extension)) {
      return `Only ${allowedLabel} files are allowed.`;
    }

    const mimeType = String(file.type || "").toLowerCase();
    if (mimeType && !isSafeFileMimeType(mimeType)) {
      return `This file type is blocked. Only ${allowedLabel} files are allowed.`;
    }

    return "";
  };
  const tryAutoUploadPendingFile = (accept) => {
    const validationError = validateSelectedFile(pfRef.current, accept);
    if (validationError) {
      clearPendingFile();
      setErrorMsg(validationError);
      setBusy(false);
      return false;
    }
    doFileUpload(pfRef.current);
    return true;
  };

  useEffect(() => {
    const handleWindowDragEnter = (e) => {
      if (!isFileDragEvent(e)) return;
      e.preventDefault();
      setDragOver(true);
    };
    const handleWindowDragOver = (e) => {
      if (!isFileDragEvent(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    };
    const handleWindowDragLeave = (e) => {
      if (!isFileDragEvent(e)) return;
      e.preventDefault();
      const leftWindow =
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight;
      if (leftWindow) clearDragState();
    };
    const handleWindowDrop = (e) => {
      if (!isFileDragEvent(e)) return;
      e.preventDefault();
      e.stopPropagation();
      clearDragState();
      const file = e.dataTransfer?.files?.[0];
      if (!file || busyRef.current) return;
      processDroppedFile(file);
    };
    const handleWindowDragEnd = () => clearDragState();

    window.addEventListener("dragenter", handleWindowDragEnter);
    window.addEventListener("dragover", handleWindowDragOver);
    window.addEventListener("dragleave", handleWindowDragLeave);
    window.addEventListener("drop", handleWindowDrop);
    window.addEventListener("dragend", handleWindowDragEnd);

    return () => {
      window.removeEventListener("dragenter", handleWindowDragEnter);
      window.removeEventListener("dragover", handleWindowDragOver);
      window.removeEventListener("dragleave", handleWindowDragLeave);
      window.removeEventListener("drop", handleWindowDrop);
      window.removeEventListener("dragend", handleWindowDragEnd);
    };
  }, []);

  const openTodoDashboard = useCallback(() => {
    setShowTodoDashboard(true);
    setShowWelcome(false);
    setIsOpen(true);
    setIsMinimized(false);
  }, []);
  const minimizeChatPopup = () => {
    setShowTodoDashboard(false);
    setShowWelcome(true);
    setIsMinimized(true);
    setIsOpen(false);
  };

  const handleTodoApproveReject = useCallback(
    (item, status) => {
      if (!item?.transaction) return;

      dispatch(resetRequest());

      if (item.isPayrollReference) {
        dispatch(
          setActionType(
            status === "Approve"
              ? ACTION_TYPES.APPROVE_PAYROLL
              : ACTION_TYPES.REJECT_PAYROLL,
          ),
        );
        dispatch(
          setActionPayload({
            Reference: item.transaction,
            PayrollIds: [],
          }),
        );
      } else {
        dispatch(setActionType(ACTION_TYPES.APPROVE_TRX));
        dispatch(
          setActionPayload({
            TransactionID: item.transaction,
            Status: status,
            IsQuickPayAmendment: item.method === "Quick Pay",
            IsPendingBankTransfer: item.account === "BSL",
          }),
        );
      }

      onOpenQrAfterGenerate?.();
    },
    [dispatch, onOpenQrAfterGenerate],
  );

  const handleTodoReview = useCallback(
    (item) => {
      if (!item) return;

      setShowTodoDashboard(false);
      setIsOpen(false);
      setIsMinimized(false);
      navigate("/pendingapprovals", {
        state: {
          assistantReviewTransactionId: buildDetailsRequestId(item),
        },
      });
    },
    [navigate],
  );

  const handleSuggestedActionSelect = useCallback(
    (label) => {
      handleQuickActionSelect(label);
    },
    [handleQuickActionSelect],
  );

  const nextMessageId = () => Date.now() + messageIdRef.current++;
  const appendMessage = (text, sender) => {
    const value =
      typeof text === "string" ? text.trim() : String(text || "").trim();
    if (!value) return null;
    const newMessage = { id: nextMessageId(), text: value, sender };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage.id;
  };
  const appendBotMessage = (text) => appendMessage(text, "bot");
  const syncRows = () => {
    try {
      const raw = cdRef.current.PayrollInfoList;
      if (raw) {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  };

  const mergeCD = (r) => {
    const d = r.CollectedData || r.collectedData || "";
    let parsed = null;
    if (d) {
      try {
        parsed = typeof d === "string" ? JSON.parse(d) : d;
      } catch {}
    }
    if (parsed && typeof parsed === "object") {
      if (parsed.AmendedExcelUrl && parsed.AmendedExcelUrl.startsWith("http"))
        excelUrlRef.current = parsed.AmendedExcelUrl;
      Object.assign(cdRef.current, parsed);
    }

    const directDownloadData = {};
    [
      "BlobUrl",
      "blobUrl",
      "DownloadUrl",
      "downloadUrl",
      "ExcelBase64",
      "excelBase64",
      "ExcelFileName",
      "excelFileName",
    ].forEach((key) => {
      if (r[key]) directDownloadData[key] = r[key];
    });
    if (Object.keys(directDownloadData).length > 0) {
      Object.assign(cdRef.current, directDownloadData);
    }

    const nextData = { ...(parsed || {}), ...directDownloadData };
    if (Object.keys(nextData).length > 0) {
      setActiveCard((card) =>
        card?.type === "success"
          ? { ...card, data: { ...card.data, ...nextData } }
          : card,
      );
    }
  };

  const stopPoll = () => {
    if (ptRef.current) {
      clearInterval(ptRef.current);
      ptRef.current = null;
    }
  };

  const failFlow = (message, { hard = false } = {}) => {
    stopPoll();
    setErrorMsg(formatApiMessageText(message || "Operation failed."));
    if (hard) {
      clearPendingFile();
      setActiveCard(null);
      setProgressSteps([]);
      authorizeContextRef.current = null;
      try {
        if (fidRef.current) api.Chat(fidRef.current, "cancel").catch(() => {});
      } catch {}
      fidRef.current = null;
      cdRef.current = {};
    }
    setBusy(false);
  };

  const buildAuthorizationPayload = (portalCall, templateAuthCode = "") => {
    const { f, rk } = rt(portalCall.Template, templateAuthCode);
    const rb = {};
    for (const [k, v] of Object.entries(f)) {
      if (rk.has(k)) {
        try {
          rb[k] = JSON.parse(v);
        } catch {
          rb[k] = v;
        }
      } else if (
        /^\d+$/.test(v) &&
        !["SessionID", "AuthorizationCode"].includes(k)
      ) {
        rb[k] = parseInt(v);
      } else {
        rb[k] = v;
      }
    }
    return rb;
  };

  const startAuthorizationPolling = (authorizationCode) => {
    stopPoll();
    let pollCount = 0;
    ptRef.current = setInterval(async () => {
      pollCount++;
      if (pollCount > 60) {
        stopPoll();
        setErrorMsg("Authorization timed out. Refresh QR to continue.");
        setBusy(false);
        return;
      }
      try {
        const p = await api.PortalCall(AUTH_POLL, {
          SessionID: api.getSid(),
          TS: Date.now(),
          AuhtorizationCode: authorizationCode,
        });
        if (hasApiError(p)) {
          failFlow(extractApiErrorMessage(p));
          return;
        }
        const st = p?.Payload?.Status || "PENDING";
        if (st === "AUTHORIZED") {
          stopPoll();
          const raw = await api.Chat(fidRef.current, authorizationCode);
          const nx = N(raw);
          if (hasApiError(nx) || hasApiError(raw)) {
            failFlow(
              extractApiErrorMessage(raw) || extractApiErrorMessage(nx),
              { hard: true },
            );
            return;
          }
          mergeCD(nx);
          await handleR(nx);
        } else if (st === "REJECTED") {
          stopPoll();
          setErrorMsg("Authorization declined.");
          setBusy(false);
        }
      } catch (error) {
        stopPoll();
        setErrorMsg(error?.message || "Unable to verify authorization status.");
        setBusy(false);
      }
    }, 2000);
  };

  const requestAuthorizationQr = async (
    portalCall,
    cardData,
    templateAuthCode = "",
  ) => {
    const rb = buildAuthorizationPayload(portalCall, templateAuthCode);
    const data = await api.PortalCall(
      portalCall.Endpoint,
      rb,
      false,
      portalCall.Method,
    );
    if (hasApiError(data)) {
      throw new Error(extractApiErrorMessage(data));
    }
    const pl = data.Payload || data;
    const authorizationCode = pl.AuthorizationCode || "";
    const qrPayload = pl.QrCode || "";

    if (!authorizationCode) {
      throw new Error(extractApiErrorMessage(data) || "QR generation failed.");
    }

    authorizeContextRef.current = { portalCall, cardData };
    cdRef.current.authCode = authorizationCode;
    if (cardData) {
      setActiveCard({
        type: "qr",
        data: { ...cardData, QrPayload: qrPayload },
      });
    }
    setErrorMsg("");
    startAuthorizationPolling(authorizationCode);
  };

  const handleRefreshQr = async () => {
    if (!authorizeContextRef.current || refreshingQr) return;

    setRefreshingQr(true);
    try {
      await requestAuthorizationQr(
        authorizeContextRef.current.portalCall,
        authorizeContextRef.current.cardData,
        "",
      );
    } catch (e) {
      setErrorMsg(e.message);
      setBusy(false);
    } finally {
      setRefreshingQr(false);
    }
  };

  const applyStepMeta = (sm) => {
    if (!sm) return;
    setProgressSteps(sm.ProgressSteps || []);
    if (sm.CardType && sm.CardData) {
      if (sm.CardType === "upload") {
        setActiveCard({ type: sm.CardType, data: sm.CardData });
        if (pfRef.current && fidRef.current) {
          setTimeout(
            () => tryAutoUploadPendingFile(sm.CardData?.AcceptedFormats),
            0,
          );
        }
        return;
      }
      setActiveCard({
        type: sm.CardType,
        data:
          sm.CardType === "success"
            ? { ...cdRef.current, ...(sm.CardData || {}) }
            : sm.CardData,
      });
    }
  };

  const rt = (tpl, ac) => {
    if (!tpl) return { f: {}, rk: new Set() };
    let o;
    try {
      o = JSON.parse(tpl);
    } catch {
      return { f: {}, rk: new Set() };
    }
    const f = {},
      rk = new Set(),
      sid = api.getSid();
    for (const [k, rv] of Object.entries(o)) {
      const raw = String(rv),
        m = raw.match(/^\{\{(.+?)\}\}$/);
      if (!m) {
        f[k] = raw;
        continue;
      }
      let e = m[1];
      if (e === "SessionID") {
        f[k] = sid;
        continue;
      }
      if (e === "AuthCode") {
        f[k] = ac || "";
        continue;
      }
      if (e === "File") {
        f[k] = "__FILE__";
        continue;
      }
      let ir = false;
      if (e.endsWith(":raw")) {
        ir = true;
        e = e.slice(0, -4);
      }
      let v = "";
      if (e.includes("|")) {
        const p = e.split("|");
        v = cdRef.current[p[0]] || "";
        for (let i = 1; i < p.length; i++) {
          const [a, b] = p[i].split(":");
          if (v === a) {
            v = b;
            break;
          }
        }
      } else v = cdRef.current[e] || "";
      f[k] = v;
      if (ir) rk.add(k);
    }
    return { f, rk };
  };

  const handleR = async (r) => {
    setErrorMsg("");
    clearStreamingPreview();

    if (hasApiError(r)) {
      stopPoll();
      setErrorMsg(
        getApiErrorMessage(r)
          .replace(/\\r\\n/g, "\n")
          .replace(/\r\n/g, "\n"),
      );
      setBusy(false);
      return;
    }

    const userFacing = [
      "question",
      "confirm",
      "summary",
      "choice",
      "ack",
      "html_result",
      "need_file",
      "redirect_page",
      "switch_merchant",
    ];
    if (r.BotMessage && userFacing.includes(r.Action))
      appendBotMessage(r.BotMessage);
    mergeCD(r);
    if (r.StepMeta) applyStepMeta(r.StepMeta);

    if (r.Action === "switch") {
      clearPendingFile();
      authorizeContextRef.current = null;
      fidRef.current = null;
      cdRef.current = {};
      stopPoll();
      setActiveCard(null);
      setProgressSteps([]);
      setBusy(false);
      busyRef.current = false;
      if (lastUserMessageRef.current)
        setTimeout(
          () => sendPrompt(lastUserMessageRef.current, { skipAppend: true }),
          0,
        );
      return;
    }
    if (r.Action === "redirect_page") {
      clearPendingFile();
      authorizeContextRef.current = null;
      fidRef.current = null;
      cdRef.current = {};
      stopPoll();
      setActiveCard(null);
      setProgressSteps([]);
      try {
        const pageData = JSON.parse(r.CollectedData);
        const pageKey = pageData.pageKey;
        if (pageKey) {
          setBusy(false);
          setTimeout(() => streamAnalyze(`go to ${pageKey}`), 500);
        }
      } catch {
        setBusy(false);
      }
      return;
    }
    if (r.Action === "switch_merchant") {
      clearPendingFile();
      stopPoll();
      authorizeContextRef.current = null;
      setActiveCard(null);
      setProgressSteps([]);
      try {
        const merchantData =
          cdRef.current && Object.keys(cdRef.current).length > 0
            ? cdRef.current
            : JSON.parse(r.CollectedData || "{}");
        const entityId = merchantData.MerchantID || merchantData.LegalEntityID;
        if (!entityId) {
          setErrorMsg("The merchant switch response is missing MerchantID.");
          setBusy(false);
          return;
        }
        await SaveUserEmployeeSelectedMerchant(merchantData);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (r.Action === "complete" || r.FlowCompleted) {
      authorizeContextRef.current = null;
      if (r.StepMeta?.CardType === "success" && r.StepMeta?.CardData) {
        setActiveCard({ type: "success", data: r.StepMeta.CardData });
        setBusy(false);
        if (r.QueuedMessage) {
          setTimeout(() => {
            appendBotMessage(`Now handling: ${r.QueuedMessage}`);
            fidRef.current = null;
            cdRef.current = {};
            setActiveCard(null);
            setProgressSteps([]);
            setBusy(true);
            busyRef.current = true;
            streamAnalyze(r.QueuedMessage);
          }, 2000);
          return;
        }
      } else {
        const cd = cdRef.current;
        const progressDone = (
          progressSteps.length > 0 ? progressSteps : []
        ).map((s) => ({ ...s, Status: "done", status: "done" }));
        if (progressDone.length > 0) setProgressSteps(progressDone);

        let summary = cd.PayrollSummary || {};
        if (typeof summary === "string") {
          try {
            summary = JSON.parse(summary);
          } catch {
            summary = {};
          }
        }
        const totalEmp = summary.TotalEmployees || cd.TotalEmployees || 0;
        const totalUSD =
          summary.TotalPayrollAmountUSD || cd.TotalPayrollAmountUSD || 0;
        const totalLBP =
          summary.TotalPayrollAmountLBP || cd.TotalPayrollAmountLBP || 0;
        const amountParts = [];
        if (Number(totalUSD) > 0)
          amountParts.push(
            `$${Number(totalUSD).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
          );
        if (Number(totalLBP) > 0)
          amountParts.push(`LL ${Number(totalLBP).toLocaleString("en-US")}`);
        const subtitle =
          r.BotMessage ||
          `Successfully processed payments for ${totalEmp} employees${amountParts.length ? " totaling " + amountParts.join(" + ") : ""}`;

        const stats = [];
        const confirmResult = cd.confirmResult;
        let parsed = null;
        if (confirmResult) {
          try {
            parsed =
              typeof confirmResult === "string"
                ? JSON.parse(confirmResult)
                : confirmResult;
          } catch {}
        }
        const pl = parsed?.Payload || parsed || {};
        if (
          pl.Processed !== undefined ||
          pl.Pending !== undefined ||
          pl.Failed !== undefined
        ) {
          stats.push({
            Label: "Processed",
            Value: String(pl.Processed ?? totalEmp),
          });
          stats.push({ Label: "Pending", Value: String(pl.Pending ?? 0) });
          stats.push({ Label: "Failed", Value: String(pl.Failed ?? 0) });
        } else {
          stats.push({ Label: "Employees", Value: String(totalEmp) });
          stats.push({ Label: "Status", Value: "Awaiting Approval" });
        }

        setActiveCard({
          type: "success",
          data: {
            ...cdRef.current,
            Title: "Payroll Transaction Complete!",
            Subtitle: subtitle,
            Stats: stats,
          },
        });
        setBusy(false);
      }
      clearPendingFile();
      fidRef.current = null;
      cdRef.current = {};
      return;
    }
    if (r.Action === "cancelled") {
      authorizeContextRef.current = null;
      resetConversationState(r.BotMessage || "Cancelled.");
      return;
    }
    if (r.Action === "need_file") {
      if (pfRef.current && fidRef.current) {
        tryAutoUploadPendingFile(activeCardRef.current?.data?.AcceptedFormats);
        return;
      }
      setBusy(false);
      return;
    }
    if (
      r.Action === "question" ||
      r.Action === "confirm" ||
      r.Action === "summary" ||
      r.Action === "html_result" ||
      r.Action === "ack"
    ) {
      setBusy(false);
      return;
    }
    if (r.Action === "portal_call" && r.PortalCall) {
      const pc = r.PortalCall;
      if (pc.Template?.includes("{{Hash}}")) {
        try {
          const h = await api.ComputeHash(fidRef.current);
          if (h.success || h.Success) cdRef.current.Hash = h.hash || h.Hash;
        } catch {}
      }
      const { f, rk } = rt(pc.Template, cdRef.current.authCode || "");
      let pr;
      try {
        if (pc.ContentType === "multipart") {
          const fd = new FormData();
          for (const [k, v] of Object.entries(f))
            if (v === "__FILE__" && pfRef.current) fd.append(k, pfRef.current);
            else fd.append(k, v);
          pr = await api.PortalCall(pc.Endpoint, fd, true, pc.Method);
        } else {
          const jb = {};
          for (const [k, v] of Object.entries(f))
            if (rk.has(k)) {
              try {
                jb[k] = JSON.parse(v);
              } catch {
                jb[k] = v;
              }
            } else jb[k] = v;
          pr = await api.PortalCall(pc.Endpoint, jb, false, pc.Method);
        }
      } catch (e) {
        failFlow(e?.message || "Network error during portal call.", {
          hard: true,
        });
        return;
      }

      const portalValidationMessage = getPortalValidationMessage(
        pc.Endpoint,
        pr,
      );
      if (portalValidationMessage) {
        setErrorMsg(formatApiMessageText(portalValidationMessage));
        setBusy(false);
        return;
      }
      const isErr = hasApiError(pr);
      if (isErr) {
        const errTxt = extractApiErrorMessage(pr);
        setErrorMsg(errTxt.replace(/\\r\\n/g, "\n").replace(/\r\n/g, "\n"));
        clearPendingFile();
        setActiveCard(null);
        setProgressSteps([]);
        authorizeContextRef.current = null;
        try {
          const cancelRaw = await api.Chat(fidRef.current, "cancel");
          if (hasApiError(cancelRaw))
            setErrorMsg(
              formatApiMessageText(extractApiErrorMessage(cancelRaw)),
            );
        } catch {}
        fidRef.current = null;
        cdRef.current = {};
        setBusy(false);
        return;
      }
      if (pr?.Payload) {
        Object.assign(cdRef.current, pr.Payload);
        if (pr.Payload.AmendedExcelUrl)
          excelUrlRef.current = pr.Payload.AmendedExcelUrl;
      }
      const raw = await api.SaveApiResponse(fidRef.current, JSON.stringify(pr));
      const nx = N(raw);
      mergeCD(nx);
      await handleR(nx);
      return;
    }
    if (r.Action === "authorize" && r.PortalCall) {
      try {
        await requestAuthorizationQr(
          r.PortalCall,
          r.StepMeta?.CardData,
          cdRef.current.authCode || "",
        );
      } catch (e) {
        setErrorMsg(e.message);
        setBusy(false);
      }
      return;
    }
    setBusy(false);
  };

  const streamAnalyze = async (msg) => {
    let streamedText = "";
    let hadFlow = false;
    let didAppendTextResponse = false;
    try {
      clearStreamingPreview();
      const res = await api.AnalyzeStream(msg);
      if (hasApiError(res)) {
        failFlow(extractApiErrorMessage(res));
        return;
      }
      const rd = res.body.getReader(),
        dc = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await rd.read();
        if (done) break;
        buf += dc.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let ev;
          try {
            ev = JSON.parse(line.slice(6).trim());
          } catch {
            continue;
          }
          if (ev.type === "text" && !hadFlow) {
            streamedText += ev.data;
            queueStreamingText(streamedText);
          }
          if (ev.type === "flow") {
            hadFlow = true;
            clearStreamingPreview();
            let parsedFlow;
            try {
              parsedFlow = JSON.parse(ev.data);
            } catch {
              parsedFlow = null;
            }
            const d = N(parsedFlow);
            if (hasApiError(d) || hasApiError(parsedFlow)) {
              failFlow(
                extractApiErrorMessage(parsedFlow) || extractApiErrorMessage(d),
              );
              return;
            }
            fidRef.current = d.FlowSessionId;
            mergeCD(d);
            await handleR(d);
          } else if (ev.type === "redirect") {
            hadFlow = true;
            clearStreamingPreview();
            const d = JSON.parse(ev.data);
            const path = d.Path || d.path;
            setErrorMsg(`Opening ${d.Name || d.name} in a new tab...`);
            setTimeout(() => window.open(path, ""), 1500);
            setBusy(false);
          } else if (ev.type === "error") {
            hadFlow = true;
            clearStreamingPreview();
            setErrorMsg("Error: " + ev.data);
            setBusy(false);
          } else if (ev.type === "done") {
            if (!hadFlow && streamedText.trim() && !didAppendTextResponse) {
              finalizeStreamingText(streamedText);
              didAppendTextResponse = true;
              setShowWelcome(false);
            }
            if (!hadFlow) setBusy(false);
          }
        }
      }
      if (!hadFlow && streamedText.trim() && !didAppendTextResponse) {
        finalizeStreamingText(streamedText);
        setShowWelcome(false);
        setBusy(false);
      }
    } catch (e) {
      clearStreamingPreview();
      setErrorMsg("Connection error: " + e.message);
      setBusy(false);
    }
  };

  const sendPrompt = async (msg, { skipAppend = false } = {}) => {
    if (busyRef.current) return;
    const text = (
      typeof msg === "string" ? msg : inputRef.current?.value || ""
    ).trim();
    if (!text) return;
    if (inputRef.current) inputRef.current.value = "";
    onChange?.("");
    lastUserMessageRef.current = text;
    if (!skipAppend) appendMessage(text, "user");
    clearStreamingPreview();
    setErrorMsg("");
    setShowTodoDashboard(false);
    setShowWelcome(false);
    setAttachedFile(null);
    setBusy(true);
    if (fidRef.current) {
      try {
        const raw = await api.Chat(fidRef.current, text);
        const r = N(raw);
        if (hasApiError(r) || hasApiError(raw)) {
          failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
          return;
        }
        mergeCD(r);
        await handleR(r);
      } catch (e) {
        setErrorMsg(e.message);
        setBusy(false);
      }
      return;
    }
    cdRef.current = {};
    await streamAnalyze(text);
  };
  // ═══ VOICE RECORDING ═══════════════════════════════════════
  const startRecording = async () => {
    if (busy || isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      audioChunksRef.current = [];
      setRecordingDuration(0);

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        if (audioBlob.size < 1000) {
          setErrorMsg("Recording too short. Hold the mic button longer.");
          setIsRecording(false);
          return;
        }
        setIsRecording(false);
        setRecordingDuration(0);
        await sendVoice(audioBlob);
      };

      recorder.start(250);
      setIsRecording(true);
      openChatPopup();

      let seconds = 0;
      recordingTimerRef.current = setInterval(() => {
        seconds++;
        setRecordingDuration(seconds);
        if (seconds >= 60) stopRecording();
      }, 1000);
    } catch (err) {
      if (err.name === "NotAllowedError")
        setErrorMsg(
          "Microphone access denied. Please allow mic in browser settings.",
        );
      else if (err.name === "NotFoundError")
        setErrorMsg("No microphone found.");
      else setErrorMsg("Could not start recording: " + err.message);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    )
      mediaRecorderRef.current.stop();
  };

  const cancelRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    try {
      mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
    } catch {}
    audioChunksRef.current = [];
    clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = null;
    setIsRecording(false);
    setRecordingDuration(0);
  };

  const sendVoice = async (audioBlob) => {
    if (busyRef.current) return;
    setBusy(true);
    busyRef.current = true;
    setErrorMsg("");
    setShowWelcome(false);
    clearStreamingPreview();

    // Mid-flow → VoiceChat (JSON response, same as Chat)
    if (fidRef.current) {
      try {
        const raw = await api.VoiceChat(fidRef.current, audioBlob);
        const r = N(raw);
        const transcript =
          r.VoiceTranscript || raw.VoiceTranscript || raw.voiceTranscript || "";
        if (transcript) {
          appendMessage(transcript + " 🎙️", "user");
          lastUserMessageRef.current = transcript;
        }
        if (hasApiError(r) || hasApiError(raw)) {
          failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
          return;
        }
        mergeCD(r);
        await handleR(r);
      } catch (e) {
        setErrorMsg("Voice processing failed: " + e.message);
        setBusy(false);
      }
      return;
    }

    // No flow → VoiceAnalyze (SSE stream, same as AnalyzeStream)
    let streamedText = "";
    let hadFlow = false;
    let didAppendTextResponse = false;
    try {
      const res = await api.VoiceAnalyze(audioBlob);
      if (!res.ok) {
        setErrorMsg("Voice processing failed. Please try typing instead.");
        setBusy(false);
        return;
      }
      const rd = res.body.getReader(),
        dc = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await rd.read();
        if (done) break;
        buf += dc.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let ev;
          try {
            ev = JSON.parse(line.slice(6).trim());
          } catch {
            continue;
          }

          if (ev.type === "voice_transcript") {
            appendMessage(ev.data + " 🎙️", "user");
            lastUserMessageRef.current = ev.data;
          } else if (ev.type === "text" && !hadFlow) {
            streamedText += ev.data;
            queueStreamingText(streamedText);
          } else if (ev.type === "flow") {
            hadFlow = true;
            clearStreamingPreview();
            let parsedFlow;
            try {
              parsedFlow = JSON.parse(ev.data);
            } catch {
              parsedFlow = null;
            }
            const d = N(parsedFlow);
            if (hasApiError(d) || hasApiError(parsedFlow)) {
              failFlow(
                extractApiErrorMessage(parsedFlow) || extractApiErrorMessage(d),
              );
              return;
            }
            fidRef.current = d.FlowSessionId;
            mergeCD(d);
            await handleR(d);
          } else if (ev.type === "redirect") {
            hadFlow = true;
            clearStreamingPreview();
            const d = JSON.parse(ev.data);
            setErrorMsg(`Opening ${d.Name || d.name}...`);
            setTimeout(() => window.open(d.Path || d.path, ""), 1500);
            setBusy(false);
          } else if (ev.type === "error") {
            hadFlow = true;
            clearStreamingPreview();
            setErrorMsg("Error: " + ev.data);
            setBusy(false);
          } else if (ev.type === "done") {
            if (!hadFlow && streamedText.trim() && !didAppendTextResponse) {
              finalizeStreamingText(streamedText);
              didAppendTextResponse = true;
              setShowWelcome(false);
            }
            if (!hadFlow) setBusy(false);
          }
        }
      }
      if (!hadFlow && streamedText.trim() && !didAppendTextResponse) {
        finalizeStreamingText(streamedText);
        setShowWelcome(false);
        setBusy(false);
      }
    } catch (e) {
      clearStreamingPreview();
      setErrorMsg("Voice connection error: " + e.message);
      setBusy(false);
    }
  };
  // ═══ END VOICE ═════════════════════════════════════════════
  const handleChoiceSelect = async (value) => {
    if (guardBusy() || !fidRef.current) return;
    try {
      const raw = await api.Chat(fidRef.current, value);
      const r = N(raw);
      if (hasApiError(r) || hasApiError(raw)) {
        failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
        return;
      }
      mergeCD(r);
      await handleR(r);
    } catch (e) {
      setErrorMsg(e.message);
      setBusy(false);
    }
  };

  const handleFileUpload = async (file) => {
    const validationError = validateSelectedFile(
      file,
      activeCardRef.current?.data?.AcceptedFormats,
    );
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    if (guardBusy() || !fidRef.current) return;
    setErrorMsg("");
    doFileUpload(file);
  };
  const doFileUpload = async (file) => {
    pfRef.current = file;
    cdRef.current.originalFileName = file.name;
    cdRef.current.fileSize = formatFileSize(file.size);
    setAttachedFile(null);
    setBusy(true);
    busyRef.current = true;
    try {
      const raw = await api.UploadFile(fidRef.current, file);
      const r = N(raw);
      if (hasApiError(r) || hasApiError(raw)) {
        failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
        return;
      }
      mergeCD(r);
      await handleR(r);
    } catch (e) {
      setErrorMsg("Upload error: " + e.message);
      setBusy(false);
    }
  };

  const handleSummaryConfirm = async () => {
    if (guardBusy() || !fidRef.current) return;
    try {
      let raw = await api.Chat(fidRef.current, "yes");
      let r = N(raw);
      if (hasApiError(r) || hasApiError(raw)) {
        failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
        return;
      }
      mergeCD(r);
      let retries = 0;
      while (
        fidRef.current &&
        retries < 3 &&
        r.StepMeta?.CardType === "summary" &&
        !r.FlowCompleted &&
        r.Action !== "complete"
      ) {
        retries++;
        raw = await api.Chat(fidRef.current, "yes");
        r = N(raw);
        if (hasApiError(r) || hasApiError(raw)) {
          failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
          return;
        }
        mergeCD(r);
      }
      await handleR(r);
    } catch (e) {
      setErrorMsg(e.message);
      setBusy(false);
    }
  };

  const handleSummaryCancel = async () => {
    if (guardBusy() || !fidRef.current) return;
    try {
      const raw = await api.Chat(fidRef.current, "cancel");
      if (hasApiError(raw)) {
        failFlow(extractApiErrorMessage(raw));
        return;
      }
    } catch {}
    resetConversationState();
  };

  const handleRowAction = async (idx, action) => {
    if (!fidRef.current) return;
    try {
      const raw = await api.Chat(fidRef.current, `row:${idx}:${action}`);
      const r = N(raw);
      if (hasApiError(r) || hasApiError(raw)) {
        failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
        return;
      }
      mergeCD(r);
      // Backend returns fresh HTML in StepMeta.CardData.Html
      if (r.StepMeta?.CardData) {
        setActiveCard({
          type: r.StepMeta.CardType || "summary",
          data: r.StepMeta.CardData,
        });
      }
      if (r.StepMeta?.ProgressSteps) setProgressSteps(r.StepMeta.ProgressSteps);
      if (r.BotMessage && (r.Action === "question" || r.Action === "summary"))
        appendBotMessage(r.BotMessage);
      // If all rows resolved, handleR will advance the flow
      if (r.Action === "question" || r.Action === "summary") return;
      await handleR(r);
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handleQrScanned = () => {
    setErrorMsg("Waiting for confirmation...");
  };

  const handleStepClick = async (stepIdx) => {
    if (busyRef.current || !fidRef.current) return;
    stopPoll();
    setBusy(true);
    try {
      const raw = await api.Chat(fidRef.current, `goto:${stepIdx}`);
      const r = N(raw);
      if (hasApiError(r) || hasApiError(raw)) {
        failFlow(extractApiErrorMessage(raw) || extractApiErrorMessage(r));
        return;
      }
      mergeCD(r);
      setActiveCard(null);
      setErrorMsg("");
      await handleR(r);
    } catch (e) {
      setErrorMsg(e.message);
      setBusy(false);
    }
  };

  const resetChat = () => {
    resetConversationState();
  };

  const formatFileSize = (b) =>
    b < 1024
      ? b + " B"
      : b < 1048576
        ? (b / 1024).toFixed(1) + " KB"
        : (b / 1048576).toFixed(2) + " MB";

  const getDetectedIntentStartPrompt = (result) => {
    const intentName = String(
      result?.IntentName || result?.intentName || "",
    ).trim();
    const intentKey = String(result?.IntentKey || result?.intentKey || "")
      .replace(/[_-]+/g, " ")
      .trim();
    const promptTarget = intentName || intentKey;

    return promptTarget ? `start ${promptTarget}` : "";
  };

  const detectAttachedFile = async (file) => {
    if (guardBusy()) return;

    const fileSize = formatFileSize(file.size);
    pfRef.current = file;
    cdRef.current.originalFileName = file.name;
    cdRef.current.fileSize = fileSize;
    setAttachedFile({ name: file.name, size: fileSize });
    setErrorMsg("");

    try {
      if (typeof api?.DetectFile !== "function") {
        throw new Error(
          "DetectFile API is unavailable. Please refresh the page.",
        );
      }
      const raw = await api.DetectFile(file);
      const payload =
        raw?.Payload && typeof raw.Payload === "object" ? raw.Payload : null;
      const normalized = N(raw);
      const normalizedPayload = payload ? N(payload) : null;
      const detectMessage = formatApiMessageText(
        payload?.Message || payload?.message || raw?.Message || raw?.message,
      );

      if (raw && typeof raw === "object") Object.assign(cdRef.current, raw);
      if (payload) Object.assign(cdRef.current, payload);

      if (hasApiError(normalized) || hasApiError(normalizedPayload)) {
        const errorMessage =
          normalizedPayload?.PopupDescription ||
          normalizedPayload?.PopupTitle ||
          normalizedPayload?.BotMessage ||
          normalizedPayload?.Message ||
          normalized?.PopupDescription ||
          normalized?.PopupTitle ||
          normalized?.BotMessage ||
          normalized?.Message ||
          raw?.Message ||
          raw?.message ||
          payload?.Message ||
          payload?.message ||
          "Unable to detect file.";
        throw new Error(formatApiMessageText(errorMessage));
      }

      if (
        detectMessage &&
        !normalized.BotMessage &&
        !normalizedPayload?.BotMessage
      ) {
        appendBotMessage(detectMessage);
        setShowWelcome(false);
      }

      const detectedIntentPrompt = getDetectedIntentStartPrompt({
        ...(raw && typeof raw === "object" ? raw : {}),
        ...(payload && typeof payload === "object" ? payload : {}),
      });

      if (
        normalized.FlowSessionId ||
        normalized.Action ||
        normalized.BotMessage ||
        normalized.StepMeta ||
        normalized.CollectedData
      ) {
        if (normalized.FlowSessionId) fidRef.current = normalized.FlowSessionId;
        mergeCD(normalized);
        await handleR(normalized);
        return;
      }

      if (detectedIntentPrompt) {
        lastUserMessageRef.current = detectedIntentPrompt;
        await streamAnalyze(detectedIntentPrompt);
        return;
      }

      setBusy(false);
    } catch (e) {
      clearPendingFile();
      setErrorMsg(formatApiMessageText(e?.message || "Unable to detect file."));
      setBusy(false);
    }
  };

  const handleDragOver = (e) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    clearDragState();
    const file = e.dataTransfer?.files?.[0];
    if (!file || busyRef.current) return;
    processDroppedFile(file);
  };
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || busy) return;
    processDroppedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const processDroppedFile = (file) => {
    const accept =
      activeCardRef.current?.type === "upload"
        ? activeCardRef.current?.data?.AcceptedFormats
        : DEFAULT_SAFE_FILE_ACCEPT;
    const validationError = validateSelectedFile(file, accept);
    if (validationError) {
      openChatPopup();
      setErrorMsg(validationError);
      return;
    }
    openChatPopup();
    if (fidRef.current && activeCardRef.current?.type === "upload") {
      handleFileUpload(file);
    } else if (!fidRef.current) {
      detectAttachedFile(file);
    } else {
      pfRef.current = file;
      cdRef.current.originalFileName = file.name;
      cdRef.current.fileSize = formatFileSize(file.size);
      setAttachedFile({ name: file.name, size: formatFileSize(file.size) });
      setErrorMsg("");
    }
  };
  const removeAttachedFile = () => {
    clearPendingFile();
  };

  const renderCard = () => {
    if (!activeCard) return null;
    switch (activeCard.type) {
      case "choice":
        return (
          <ChoiceCard
            data={activeCard.data}
            onSelect={handleChoiceSelect}
            disabled={busy}
          />
        );
      case "upload":
        return (
          <UploadCard
            data={activeCard.data}
            onFileSelected={handleFileUpload}
            disabled={busy}
            acceptedFormats={getSafeAcceptString(
              activeCard.data?.AcceptedFormats,
            )}
          />
        );
      case "summary":
        return (
          <SummaryCard
            data={activeCard.data}
            onRowAction={handleRowAction}
            disabled={busy}
          />
        );
      case "qr":
        return (
          <QrCard
            data={activeCard.data}
            onScanned={handleQrScanned}
            disabled={busy}
            onRefresh={handleRefreshQr}
            refreshing={refreshingQr}
          />
        );
      case "success":
        return (
          <SuccessCard data={activeCard.data} collectedData={activeCard.data} />
        );
      case "question":
      default:
        return <QuestionCard data={activeCard.data} />;
    }
  };

  const isSummaryCard = activeCard?.type === "summary";
  const isSuccessCard = activeCard?.type === "success";
  const showInlineComposerFlow = !isSummaryCard && !isSuccessCard;
  const hasFlow =
    !showWelcome && (activeCard || messages.length > 0 || errorMsg);
  const summaryCancelLabel =
    activeCard?.data?.CancelLabel || activeCard?.data?.cancelLabel || "Cancel";
  const summaryConfirmLabel =
    activeCard?.data?.ConfirmLabel ||
    activeCard?.data?.confirmLabel ||
    "Confirm & Proceed";
  const isUploadCard = activeCard?.type === "upload";
  const isPopupVisible = isOpen && !isMinimized;
  const showThinkingIndicator =
    busy && !streamingBotText && !activeCard && !errorMsg;
  const wrapperClassName = [
    "ai-assistant-wrapper",
    isPopupVisible ? "ai-assistant-wrapper--popup-open" : "",
    variant ? `ai-assistant-wrapper--${variant}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const popupPlaceholderHeight =
    showTodoDashboard || messages.length > 0 ? "650px" : "300px";
  const wrapperStyle = isPopupVisible
    ? {
        "--chat-inline-height": popupPlaceholderHeight,
        "--chat-inline-min-height": popupPlaceholderHeight,
        "--chat-inline-max-height": popupPlaceholderHeight,
      }
    : {
        "--chat-inline-height": "100%",
        "--chat-inline-min-height": "300px",
        "--chat-inline-max-height": "650px",
      };

  return (
    <>
      {isPopupVisible && (
        <div className="ai-popup-backdrop" onClick={minimizeChatPopup} />
      )}
      {isPopupVisible && (
        <div
          className="ai-assistant-placeholder"
          style={wrapperStyle}
          aria-hidden="true"
        />
      )}
      <section className={wrapperClassName} style={wrapperStyle}>
        {dragOver && (
          <div className="ai-drag-overlay">
            <div className="ai-drag-overlay-card">
              <SparkleIcon width="30" height="30" />
              <h3 className="ai-drag-overlay-title">Drop your file here</h3>
              <p className="ai-drag-overlay-subtitle">
                Upload an XLSX, XLS, CSV, or PDF file to continue.
              </p>
            </div>
          </div>
        )}
        <div className="ai-container">
          {isPopupVisible && variant === "account-summary" ? (
            <div className="ai-modal-header">
              <div className="ai-modal-header__brand">
                <img src="/Images/yool.svg" alt="" />
                <div>
                  <strong>YOOL</strong>
                  <span>
                    <i aria-hidden="true" />
                    Always here to help
                  </span>
                </div>
              </div>
              <div className="ai-modal-header__actions">
                <button type="button" onClick={resetChat}>
                  + New chat
                </button>
                <button
                  type="button"
                  onClick={minimizeChatPopup}
                  aria-label="Minimize chat"
                >
                  <MinimizeIcon expanded={isPopupVisible} />
                </button>
              </div>
            </div>
          ) : null}
          <div className="welcome-bar">
            <div className="welcome-left">
              <p className="welcome-tag">
                {variant === "account-summary"
                  ? dashboardGreeting
                  : `Welcome back, ${userName}`}{" "}
                <span aria-hidden="true">👋</span>
              </p>
              <h2 className="main-title">
                {variant === "account-summary" ? (
                  "What can I help you with today?"
                ) : (
                  <>
                    Describe the <span className="accent">Action</span> You Want
                    to Create
                  </>
                )}
              </h2>
              {variant === "account-summary" ? (
                <p className="welcome-subtitle">
                  I can help you set up your workspace and complete your first
                  financial tasks.
                </p>
              ) : null}
            </div>
            <div className="welcome-actions">
              <button className="reset-btn" onClick={resetChat}>
                <SparkleIcon width="16" height="16" stroke="#FFFFFF" />
                New Chat
              </button>
            </div>
          </div>
          {!isPopupVisible && shouldShowTodoPreview ? (
            <AiTodoPreview count={pendingCount} onOpen={openTodoDashboard} />
          ) : null}
          {isPopupVisible && (
            <button
              type="button"
              className="popup-minimize-btn"
              onClick={minimizeChatPopup}
              aria-label="Minimize chat"
            >
              <MinimizeIcon expanded={isPopupVisible} />
            </button>
          )}

          <div ref={scrollAreaRef} className="ai-scroll-area">
            {showTodoDashboard ? (
              <div className="ai-todo-dashboard">
                <div className="ai-todo-dashboard__intro">
                  <h3>
                    {dashboardGreeting} <span aria-hidden="true">👋</span>
                  </h3>
                  <p>
                    My name is Yool, your AI Financial Assistant. I found items
                    that are waiting for approval.
                  </p>
                </div>

                <div className="ai-todo-dashboard__message">
                  <SparkleAvatar className="ai-todo-dashboard__avatar" />
                  <div className="ai-todo-dashboard__bubble">
                    <p>You have items that need review and approval:</p>
                    <div className="ai-todo-dashboard__cards">
                      {pendingApprovals.map((item) => (
                        <PendingApprovalCard
                          key={item.id}
                          item={item}
                          busy={busy}
                          onApprove={(approval) =>
                            handleTodoApproveReject(approval, "Approve")
                          }
                          onReject={(approval) =>
                            handleTodoApproveReject(approval, "Reject")
                          }
                          onReview={handleTodoReview}
                        />
                      ))}
                    </div>
                  </div>
                </div>

              </div>
            ) : null}

            {!showTodoDashboard && messages.length > 0 && (
              <div className="chat-messages">
                {messages.map((msg) =>
                  msg.sender === "user" ? (
                    <div key={msg.id} className="user-msg-row">
                      <div className="user-msg-bubble">{msg.text}</div>
                    </div>
                  ) : (
                    <div key={msg.id} className="composer-bot-row">
                      <div className="bot-card-content">
                        <div className="bot-text-bubble">{msg.text}</div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

            {!showTodoDashboard && streamingBotText && (
              <div className="composer-bot-row">
                <div className="bot-card-content">
                  <div className="bot-text-bubble bot-text-bubble--streaming">
                    {streamingBotText}
                  </div>
                </div>
              </div>
            )}

            {!showTodoDashboard && showThinkingIndicator && (
              <div className="composer-bot-row">
                <div className="bot-card-content">
                  <div
                    className="bot-thinking-bubble"
                    aria-label="Assistant is thinking"
                  >
                    <span className="bot-thinking-dot" />
                  </div>
                </div>
              </div>
            )}

            {!showTodoDashboard && progressSteps.length > 0 && (
              <div className="progress-row">
                <SparkleAvatar className="progress-avatar" />
                <div className="bot-card-content">
                  <ProgressTracker
                    steps={progressSteps}
                    onStepClick={handleStepClick}
                  />
                </div>
              </div>
            )}

            {!showTodoDashboard && isSummaryCard && activeCard && (
              <div className="bot-msg-row">
                <SparkleAvatar className="bot-avatar" />
                <div className="bot-card-content">{renderCard()}</div>
              </div>
            )}

            {!showTodoDashboard && isSuccessCard && (
              <div className="progress-row">
                <SparkleAvatar className="progress-avatar" />
                <div className="bot-card-content">{renderCard()}</div>
              </div>
            )}

            {!showTodoDashboard && errorMsg && (
              <div className="composer-bot-row">
                <div className="bot-card-content">
                  <div className="bot-text-bubble">{errorMsg}</div>
                </div>
              </div>
            )}
          </div>

          {isPopupVisible && variant === "account-summary" ? (
            <AiSuggestedActions onSelect={handleSuggestedActionSelect} />
          ) : null}

          <div
            className={`composer-stack${isUploadCard ? " composer-stack--upload" : ""}`}
          >
            {!showTodoDashboard &&
              showInlineComposerFlow &&
              activeCard &&
              !isUploadCard && (
                <div className="composer-inline-card">{renderCard()}</div>
              )}
            {!showTodoDashboard && showInlineComposerFlow && isUploadCard && (
              <div className="upload-container-wrapper composer-inline-card">
                {renderCard()}
              </div>
            )}

            {!showTodoDashboard && isSummaryCard && (
              <div className="action-buttons-outside">
                <button
                  className="abo-cancel"
                  onClick={handleSummaryCancel}
                  disabled={busy}
                >
                  {summaryCancelLabel}
                </button>
                <button
                  className="abo-confirm"
                  onClick={handleSummaryConfirm}
                  disabled={busy}
                >
                  {busy ? (
                    <>
                      <span className="abo-spinner" /> Processing...
                    </>
                  ) : (
                    <>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <circle
                          cx="10"
                          cy="10"
                          r="8"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M7 10l2 2 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {summaryConfirmLabel}
                    </>
                  )}
                </button>
              </div>
            )}

            <div
              className={`search-bar-group${dragOver ? " search-bar-dragover" : ""}`}
              onClick={() => {
                if (!isPopupVisible) openChatPopup();
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="search-bar-inner">
                {isSuccessCard && (
                  <button
                    type="button"
                    className="start-new-task-btn start-new-task-btn--inline"
                    onClick={resetChat}
                  >
                    <SparkleIcon
                      width="16"
                      height="16"
                      className="start-new-task-icon sparkle-sm"
                    />
                    Start New Task
                  </button>
                )}
                {attachedFile && !isSummaryCard && !isUploadCard && (
                  <div className="file-chip">
                    <div className="file-chip-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"
                          stroke="#5C4096"
                          strokeWidth="1.33"
                        />
                        <path
                          d="M9 1v4h4"
                          stroke="#5C4096"
                          strokeWidth="1.33"
                        />
                      </svg>
                    </div>
                    <div className="file-chip-info">
                      <span className="file-chip-name">
                        {attachedFile.name}
                      </span>
                      <span className="file-chip-size">
                        {attachedFile.size}
                      </span>
                    </div>
                    <button
                      className="file-chip-remove"
                      onClick={removeAttachedFile}
                      title="Remove file"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <line
                          x1="4"
                          y1="4"
                          x2="12"
                          y2="12"
                          stroke="#9CA9B3"
                          strokeWidth="1.33"
                          strokeLinecap="round"
                        />
                        <line
                          x1="12"
                          y1="4"
                          x2="4"
                          y2="12"
                          stroke="#9CA9B3"
                          strokeWidth="1.33"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}
                <div className="input-row">
                  <div className="left-icon">
                    <img
                      src="/Images/yool.svg"
                      alt=""
                      className="assistant-input-logo"
                    />
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    defaultValue=""
                    onChange={(event) => onChange?.(event.target.value)}
                    onClick={openChatPopup}
                    onFocus={openChatPopup}
                    onKeyDown={(e) => {
                      if (
                        !isPopupVisible &&
                        (e.key.length === 1 || e.key === "Backspace")
                      )
                        openChatPopup();
                      if (e.key === "Enter" && !busy) {
                        e.preventDefault();
                        sendPrompt();
                      }
                    }}
                    placeholder={
                      dragOver
                        ? "Drop your file here..."
                        : hasFlow
                          ? "Ask a follow-up question or start a new task..."
                          : variant === "account-summary"
                            ? "i want to process the payroll for my 345 employees"
                            : "I want to pay the payroll"
                    }
                    className="ai-input-field"
                    disabled={busy}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept={DEFAULT_SAFE_FILE_ACCEPT}
                    onChange={handleFileInputChange}
                  />
                  <div className="right-actions">
                    <button
                      className="attach-btn"
                      title="Attach file"
                      disabled={busy}
                      onClick={() => {
                        openChatPopup();
                        fileInputRef.current?.click();
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.33"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </svg>
                    </button>
                    <button
                      className={`mic-btn${isRecording ? " mic-btn--recording" : ""}`}
                      title={
                        isRecording
                          ? `Recording... ${recordingDuration}s`
                          : "Voice input"
                      }
                      disabled={busy && !isRecording}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isRecording) stopRecording();
                        else startRecording();
                      }}
                    >
                      {isRecording ? (
                        <div className="mic-recording-indicator">
                          <span className="mic-pulse" />
                          <span className="mic-duration">
                            {recordingDuration}s
                          </span>
                        </div>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.33"
                        >
                          <path
                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-20a3 3 0 00-3 3v8a3 3 0 006 0V5a3 3 0 00-3-3z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      className="send-btn"
                      onClick={() => sendPrompt()}
                      disabled={busy}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="1.33"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {quickActions.length > 0 && !showTodoDashboard && !isPopupVisible && (
            <div className="assistant-quick-actions" aria-label="Quick Actions">
              <p className="assistant-quick-actions__label">Quick Actions</p>
              <div className="assistant-quick-actions__grid">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={`assistant-quick-action-card is-${
                      action.tone || "blue"
                    }`}
                    onClick={() => {
                      if (onQuickActionClick) {
                        onQuickActionClick(action);
                        return;
                      }

                      handleQuickActionSelect(action.prompt);
                    }}
                  >
                    <span
                      className="assistant-quick-action-card__icon"
                      aria-hidden="true"
                    >
                      {action.iconSrc ? (
                        <img src={action.iconSrc} alt="" />
                      ) : (
                        <FontAwesomeIcon icon={action.icon} />
                      )}
                    </span>
                    <span className="assistant-quick-action-card__copy">
                      <strong>{action.title}</strong>
                      <small>{action.description}</small>
                    </span>
                    <span
                      className="assistant-quick-action-card__arrow"
                      aria-hidden="true"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default AssistantSearch;
